import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { Input } from 'components/common/Input'
import { Separator } from 'components/common/Separator'
import { Spinner } from 'components/common/Spinner'
import { Step } from 'components/common/Step'
import { Stepper } from 'components/common/Stepper'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { ComponentProps, useState } from 'react'
import { useOverlay } from '../../contexts'

type Props = {
  showAgainHandler: (e: React.ChangeEvent<HTMLInputElement>) => void
  href?: string
  isSell?: boolean
}

export const HowItWorks = ({ showAgainHandler, href, isSell }: Props) => {
  const { t } = useTranslation('common')
  const { setIsOpen } = useOverlay()
  const [isLoading, setIsLoading] = useState(false)

  const buySteps: ComponentProps<typeof Step>[] = [
    {
      id: 1,
      title: t('buy_1_title'),
      description: (
        <div>
          <span>{t('buy_1_description')}</span>
          <ol className='list-outside list-decimal pl-5'>
            <li>
              <Trans
                i18nKey='common:buy_1_description_1'
                components={{ b: <b /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey='common:buy_1_description_2'
                components={{ b: <b /> }}
              />
            </li>
          </ol>
        </div>
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 2,
      title: t('buy_2_title'),
      description: (
        <Trans i18nKey='common:buy_2_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 3,
      title: t('buy_3_title'),
      description: (
        <Trans i18nKey='common:buy_3_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
  ]

  const sellSteps: ComponentProps<typeof Step>[] = [
    {
      id: 1,
      title: t('sell_1_title'),
      description: (
        <div className='flex flex-col'>
          <span>{t('sell_1_description')}</span>
          <ol className='list-outside list-decimal pl-5'>
            <li>
              <Trans
                i18nKey='common:sell_1_description_1'
                components={{ b: <b /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey='common:sell_1_description_2'
                components={{ b: <b /> }}
              />
            </li>
          </ol>
        </div>
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 2,
      title: t('sell_2_title'),
      description: (
        <Trans i18nKey='common:sell_2_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 3,
      title: t('sell_3_title'),
      description: (
        <Trans i18nKey='common:sell_3_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
  ]

  const onUnderstand = () => {
    if (!href) {
      setIsOpen(false)
      return
    }

    setIsLoading(true)
  }

  return (
    <div className='relative flex h-full flex-col justify-between overflow-auto bg-cool-50 sm:h-auto sm:w-[475px] sm:rounded-lg'>
      <Icon
        name='close'
        size={24}
        className='absolute right-6 top-6 cursor-pointer text-cool-600 duration-200 ease-in-out hover:scale-125'
        onClick={() => setIsOpen(false)}
      />
      <div>
        <header className='px-8 py-6'>
          <h5>{isSell ? t('how_selling_works') : t('how_buying_works')}</h5>
          <p className='mt-1 text-sm text-cool-600'>
            {t('see_more_information')}{' '}
            <Link
              href='/terms-and-conditions'
              target='_blank'
              rel='norefferer'
              className='text-secondary-default'
            >
              {t('terms_&_conditions')}
            </Link>
          </p>
        </header>
        <Separator />

        {/* Restricted Country Message */}
        {/* {isSell && (
          <div className='mx-8 mt-6 flex items-start justify-center space-x-2 bg-warning-default bg-opacity-10 px-4 py-3'>
            <Icon
              name='filled-alert-info'
              filled
              size={16}
              className='mt-[4px] rotate-180 text-warning-default'
            />
            <p className='text-sm'>
              {t('selling_restricted')}{' '}
              <span className='text-base'>
                <ReactCountryFlag countryCode='GB' svg />
              </span>{' '}
              {t('expanding_soon')}
            </p>
          </div>
        )} */}

        <Stepper className='px-8 py-6' steps={isSell ? sellSteps : buySteps} />

        {isSell && (
          <div className='mx-8 mb-8 flex justify-center gap-4 rounded border border-solid border-cool-400 p-4'>
            <Icon
              name='filled-alert-info'
              filled
              size={24}
              className='text-secondary-default'
            />
            <div>
              <p className='mb-1 font-medium text-cool-900'>
                {t('condition_policy')}
              </p>
              <p className='text-sm text-cool-600'>
                {t('marketplace_integrity')}
              </p>
            </div>
          </div>
        )}
      </div>

      <footer className='sticky bottom-0 w-full bg-cool-50 shadow-[0_2px_20px_0_rgba(38,35,56,0.15)] md:shadow-none'>
        {isSell && <Separator className='w-full bg-cool-400' />}

        <div className='px-8 py-6'>
          <div className='flex items-center justify-center space-x-3'>
            <Input
              id={isSell ? 'dont-show-again-sell' : 'dont-show-again-buy'}
              data-e2e={isSell ? 'dont-show-again-sell' : 'dont-show-again-buy'}
              type='checkbox'
              onChange={e => showAgainHandler(e)}
            />
            <label
              htmlFor={isSell ? 'dont-show-again-sell' : 'dont-show-again-buy'}
              className='cursor-pointer'
            >
              {t('dont_show_again')}
            </label>
          </div>
          <Button
            onClick={onUnderstand}
            href={href}
            type={href ? 'link' : 'button'}
            size='lg'
            className='mt-4 w-full'
            data-e2e='understand-button'
          >
            {isLoading ? <Spinner theme='white' /> : t('common:i_understand')}
          </Button>
        </div>
      </footer>
    </div>
  )
}
